import { Pressable } from '../primitives/Pressable.js';
import { cx } from '../primitives/cx.js';
import { Icon } from './Icon.js';

export type CompletionToggleProps = {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  size?: 'md' | 'lg';
  /**
   * Kept, but at the floor rather than in full.
   *
   * Drawn as a filled centre inside an open ring: unmistakably *kept* at a
   * glance, and unmistakably not the same mark as a full day. Never carried by
   * colour alone — the shape differs, and `aria-checked` says `mixed`, which is
   * the tri-state a checkbox already has words for.
   */
  partial?: boolean;
};

/**
 * The single most-tapped control in the product — a student touches this more
 * than everything else combined. It gets its own component so the hit area,
 * the check-in animation and the a11y contract are decided once.
 *
 * Deliberately NOT a native checkbox: completing a habit is an achievement
 * moment, and it needs to feel like one.
 */
export function CompletionToggle({
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
  partial = false,
}: CompletionToggleProps) {
  const box = size === 'lg' ? 'size-8' : 'size-7';
  const floor = checked && partial;

  return (
    <Pressable
      role="checkbox"
      aria-checked={floor ? 'mixed' : checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="min-h-11 min-w-11 -m-2 flex items-center justify-center rounded-full p-2"
    >
      <span
        className={cx(
          box,
          'grid place-items-center rounded-full border-2',
          'transition-colors duration-[var(--aj-duration-base)] ease-standard',
          floor
            ? 'border-accent bg-transparent text-accent'
            : checked
              ? 'border-accent bg-accent text-on-accent'
              : 'border-line-strong bg-transparent text-transparent',
        )}
      >
        {floor ? (
          // A solid core inside the ring. Reads as "kept, and less than the
          // whole" without a second colour and without a tick that would claim
          // the day was finished.
          <span className={cx(size === 'lg' ? 'size-3' : 'size-2.5', 'rounded-full bg-accent')} />
        ) : (
          <Icon
            name="check"
            size={size === 'lg' ? 18 : 16}
            className={cx(
              'transition-transform duration-[var(--aj-duration-base)]',
              checked ? 'scale-100 ease-spring' : 'scale-50',
              'motion-reduce:transition-none motion-reduce:scale-100',
            )}
          />
        )}
      </span>
    </Pressable>
  );
}
